import { useTranslation } from 'react-i18next';

import type { TutorStudentUsage } from '../../../types/tutorAi';
import { formatCount, formatDate } from '../../../utils/format';
import { KpiCard, KpiSkeletonGrid } from '../../atoms/KpiCard';

/** Grid compartilhado entre a linha real e a de carregamento, para o layout não mudar. */
const GRID_CLASS = 'grid grid-cols-1 gap-mini sm:grid-cols-2 lg:grid-cols-4';

/** Quantidade de indicadores da linha — espelhada no estado de carregamento. */
const KPI_COUNT = 4;

export interface AggregateUsageKpiRowProps {
  readonly usage: TutorStudentUsage;
  /** Início do período filtrado, ISO `YYYY-MM-DD` — exibido sob o total. */
  readonly periodStart: string;
  /** Fim do período filtrado, ISO `YYYY-MM-DD` — exibido sob o total. */
  readonly periodEnd: string;
  /** Exibe placeholders no lugar dos indicadores enquanto o período é consultado. */
  readonly isLoading: boolean;
}

/**
 * Linha de indicadores do consumo agregado de tokens.
 *
 * Só o primeiro card responde ao filtro de data. `dailyTokensUsed`, `weeklyTokensUsed` e
 * `monthlyTokensUsed` são sempre dia, semana e mês **correntes** — a API os devolve assim
 * independente do período pedido. Lado a lado sob uma barra de datas eles seriam lidos como
 * recortes do período, então cada um carrega o aviso de que não o segue, e o card do
 * período carrega as datas consultadas.
 *
 * Os limites do payload (`dailyLimit`, `limitEnabled`, `*LimitReached`) não aparecem aqui:
 * são configuração por aluno e não têm significado no agregado.
 */
export function AggregateUsageKpiRow({
  usage,
  periodStart,
  periodEnd,
  isLoading,
}: AggregateUsageKpiRowProps) {
  const { t } = useTranslation('tutorAI');

  if (isLoading) {
    return <KpiSkeletonGrid gridClass={GRID_CLASS} count={KPI_COUNT} />;
  }

  const currentHint = t('usage.tokens.currentHint');

  return (
    <div className={GRID_CLASS}>
      <KpiCard
        label={t('usage.tokens.period')}
        value={formatCount(usage.periodTokensUsed)}
        hint={t('usage.tokens.periodHint', {
          start: formatDate(periodStart),
          end: formatDate(periodEnd),
        })}
        emphasis
      />
      <KpiCard
        label={t('usage.tokens.today')}
        value={formatCount(usage.dailyTokensUsed)}
        hint={currentHint}
      />
      <KpiCard
        label={t('usage.tokens.week')}
        value={formatCount(usage.weeklyTokensUsed)}
        hint={currentHint}
      />
      <KpiCard
        label={t('usage.tokens.month')}
        value={formatCount(usage.monthlyTokensUsed)}
        hint={currentHint}
      />
    </div>
  );
}
